import React, { useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import { GlobalStyles } from '@mui/system';
import { ThemeProvider } from '@mui/material/styles';
import { MaterialReactTable } from 'material-react-table';
import Avatar from '@mui/material/Avatar';
import toast from 'react-hot-toast';
import LeadService from '../../core/services/leadsService';
import { LinearProgress } from '@mui/material';
import { Box, Button, TextField, InputAdornment, Card } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon, SettingsSystemDaydreamSharp } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import Pagination from '@mui/material/Pagination';
import { debounce, throttle } from 'lodash';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { Autocomplete } from '@mui/material';
import LogoutExpiredModal from '../../components/LogoutExpiredModal';

const useStyles = makeStyles((theme) => ({
    LeadTable: {
        overflowX: 'auto',
        width: 'auto',
        borderRadius: '22px',
        boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    },
}));

const ReferralAgent = () => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const [Loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [allReferralAgent, setAllReferralAgent] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [selectedUserType, setSelectedUserType] = useState('');
    const [selectedImportType, setSelectedImportType] = useState('');
    const [logOutModal, setLogOutModal] = React.useState(false);

    const abortControllerRef = useRef(null);
    const optionsUserType = [
        { label: 'ICA Agent', value: 'isa_agent' },
        { label: 'Agent', value: 'agent' },
        { label: 'Customer', value: 'customer' },
    ];
    const optionsImportType = [
        { label: 'Organization', value: 'organization' },
        { label: 'Self', value: 'self' },
        { label: 'Import', value: 'import' },
        { label: 'Contact', value: 'contact' },
    ];
    const GetReferralAgentData = async (currentPage, searchTerm, selectedUserType, selectedImportType) => {
        try {
            const isPaginated = true;
            setLoading(true);
            const allUsers = true;
            const res = await LeadService.GetReferralAgentData(isPaginated, currentPage, searchTerm, allUsers, selectedUserType, selectedImportType);
            if (res?.data?.success) {
                setAllReferralAgent(res?.data?.data?.data);
                setCurrentPage(res?.data?.data?.current_page || 1);
                setTotalPages(Math.ceil((res?.data?.data?.total || 0) / (res?.data?.data?.per_page || 1)));
                return;
            }
        } catch (error) {
            if (error.message === 'You have been logged out of the application, please login again to use the application.') {
                setLogOutModal(true);
            } else {
                toast.error(error.message);
            }
        } finally {
            setIsLoading(false);
            setLoading(false);
        }
    };

    const columns = [
        {
            accessorKey: 'id',
            header: 'Sr no',
            size: 50,
            muiTableHeadCellProps: {
                sx: {
                    fontSize: '14px',
                },
            },
        },
        {
            accessorKey: 'name',
            header: 'User Name',
            Cell: ({ row }) => (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <div>
                        <Avatar src={`https://iconnctapp.com/` + row?.original?.profile_image} />
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <span style={{ marginLeft: 10 }}>
                            {row?.original?.name || null} {row?.original?.last_name || null}
                        </span>
                    </div>
                </div>
            ),
            enableColumnActions: false,
            showColumnFilters: false,
            enableColumnFilter: false,
            enableSorting: false,
            size: 200,
            muiTableHeadCellProps: {
                align: 'start',
            },
        },
        {
            accessorKey: 'email',
            header: 'Email',
            enableColumnActions: false,
            showColumnFilters: false,
            enableColumnFilter: false,
            enableSorting: false,
            muiTableHeadCellProps: {
                sx: {
                    fontSize: '14px',
                },
            },
            size: 'auto',
        },

        {
            accessorKey: 'user_type',
            header: 'User Type',
            enableColumnActions: false,
            showColumnFilters: false,
            enableColumnFilter: false,
            enableSorting: false,
            muiTableHeadCellProps: {
                sx: {
                    fontSize: '14px',
                },
            },
            size: 'auto',
            Cell: ({ cell }) => {
                const userType = cell.getValue();
                if (!userType) return '';

                // Handle specific formatting for "isa_agent"
                if (userType === 'isa_agent') {
                    return 'Ica Agent';
                }

                // General formatting for other user types
                const formattedUserType = userType
                    .split('_')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(' ');

                return formattedUserType;
            },
        },

        {
            accessorKey: 'referral_code',
            header: 'Referral Code',
            enableColumnActions: false,
            showColumnFilters: false,
            enableColumnFilter: false,
            enableSorting: false,
            muiTableHeadCellProps: {
                sx: {
                    fontSize: '14px',
                },
            },
            size: 'auto',
        },
        {
            accessorKey: 'import',
            header: 'Import Type',
            enableColumnActions: false,
            showColumnFilters: false,
            enableColumnFilter: false,
            enableSorting: false,
            muiTableHeadCellProps: {
                sx: {
                    fontSize: '14px',
                },
            },
            size: 'auto',
            Cell: ({ cell }) => {
                const importValue = cell.getValue();
                return importValue
                    ? importValue.charAt(0).toUpperCase() + importValue.slice(1) // Capitalize first letter
                    : '';
            },
        },
        {
            id: 'actions',
            enableSorting: false,
            header: 'See More',
            size: 100,
            Cell: ({ row }) => (
                <div style={{ display: 'flex' }}>
                    <IconButton aria-label="view" onClick={() => handleNavigateReferralDetails(
                        row?.original?.referral_code,
                        row?.original?.name,
                        row?.original?.last_name
                        )}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <VisibilityIcon />
                        </div>
                    </IconButton>
                </div>
            ),
        },
    ];
    const throttledAllReferralAgent = useCallback(
        throttle((currentPage, searchTerm, selectedUserType, selectedImportType) => {
            GetReferralAgentData(currentPage, searchTerm, selectedUserType, selectedImportType);
        }, 2000),
        []
    );

    const fetchAllAllReferralAgent = useCallback(
        debounce((currentPage, searchTerm, selectedUserType, selectedImportType) => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            const abortController = new AbortController();
            abortControllerRef.current = abortController;
            throttledAllReferralAgent(currentPage, searchTerm, selectedUserType, selectedImportType, abortController.signal);
        }, 500),
        [throttledAllReferralAgent]
    );
    useEffect(() => {
        fetchAllAllReferralAgent(currentPage, searchTerm, selectedUserType, selectedImportType);
    }, [currentPage, searchTerm, selectedUserType, selectedImportType]);

    const handleClear = () => {
        setSearchTerm('');
        setCurrentPage(1);
        localStorage.removeItem('searchTerm');
        fetchAllAllReferralAgent(1, '');
    };
    const handleNavigateReferralDetails = (referral_code,name,last_name) => {
        navigate(`/referralAgentDetails/${referral_code}`,{
            state: {
                name: name,
                last_name: last_name,
            },
        });
    };
    const handleSearchTerm = (event) => {
        const newSearchTerm = event.target.value;
        setCurrentPage(1);
        if (newSearchTerm) {
            setSearchTerm(newSearchTerm);
            localStorage.setItem('searchTerm', JSON.stringify(newSearchTerm));
        } else {
            localStorage.removeItem('searchTerm');
        }
    };

    const HandleChangeUserType = (event, newValue) => {
        const userLocalStorage = newValue ? newValue.value : '';
        if (userLocalStorage) {
            setSelectedUserType(userLocalStorage);
            localStorage.setItem('selectedUserType', JSON.stringify(userLocalStorage));
        } else {
            setSelectedUserType(null);
            localStorage.removeItem('selectedUserType');
        }
    };
    const HandleChangeImportType = (event, newValue) => {
        const importTypeLocalStorage = newValue ? newValue.value : '';
        if (importTypeLocalStorage) {
            setSelectedImportType(importTypeLocalStorage);
            localStorage.setItem('selectedImportType', JSON.stringify(importTypeLocalStorage));
        } else {
            setSelectedImportType(null);
            localStorage.removeItem('selectedImportType');
        }
    };

    useEffect(() => {
        const savedSearchTerm = localStorage.getItem('searchTerm');
        if (savedSearchTerm) {
            setSearchTerm(JSON.parse(savedSearchTerm));
        }

        const storedUserType = localStorage.getItem('selectedUserType');
        if (storedUserType) {
            setSelectedUserType(JSON.parse(storedUserType));
        }

        const storedImportType = localStorage.getItem('selectedImportType');
        if (storedImportType) {
            setSelectedImportType(JSON.parse(storedImportType));
        }
    });

    const clearALLState = () => {
        localStorage.removeItem('selectedImportType');
        localStorage.removeItem('selectedUserType');
        localStorage.removeItem('searchTerm');
        setSelectedImportType(null);
        setSelectedUserType(null);
        setSearchTerm('');
    }
    return (
        <div>
            <div style={{ marginBottom: '15px' }}> {Loading && <LinearProgress />}</div>
            <Box style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                <div>
                    <span style={{ fontWeight: 'bold', fontSize: '22px' }}>Users</span>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Autocomplete
                        options={optionsImportType}
                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => <TextField {...params} label="Import Type" />}
                        value={optionsImportType.find((option) => option.value === selectedImportType) || null}
                        onChange={HandleChangeImportType}
                        disabled={isLoading}
                        className={classes.superAdminAgentFilter}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                            },
                            width: 300,
                        }}
                    />
                    <Autocomplete
                        options={optionsUserType}
                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => <TextField {...params} label="User Type" />}
                        value={optionsUserType.find((option) => option.value === selectedUserType) || null}
                        onChange={HandleChangeUserType}
                        disabled={isLoading}
                        className={classes.superAdminAgentFilter}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                            },
                            width: 300,
                        }}
                    />
                    <TextField
                        value={searchTerm}
                        onChange={handleSearchTerm}
                        sx={{
                            width: 300,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                            },
                        }}
                        placeholder="Search..."
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleClear} aria-label="clear search">
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        variant="outlined"
                    />
                    <Button
                        style={{
                            width: 'auto',
                            height: '40px',
                            border: '1px solid gray',
                            background: '#bababa',
                            borderRadius: '8px',
                            fontWeight: 'bold',
                            marginLeft: '5px',
                            cursor: 'pointer',
                            marginTop: '5px',
                            marginBottom: 'px',
                            color: 'black',
                        }}
                        sx={{ fontFamily: 'inherit' }}
                        onClick={clearALLState}
                    >
                        Clear
                    </Button>
                </div>
            </Box>

            <ThemeProvider theme={theme}>
                <GlobalStyles
                    styles={{
                        '::-webkit-scrollbar': {
                            width: '14px',
                        },
                        '::-webkit-scrollbar-thumb': {
                            backgroundColor: '#c1c1c1',
                            borderRadius: '10px',
                        },
                    }}
                />
                <Card style={{ marginTop: '10px', borderRadius: '22px', boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px' }}>
                    <MaterialReactTable
                        columns={columns}
                        data={allReferralAgent}
                        state={{ isLoading: isLoading }}
                        muiCircularProgressProps={{
                            color: 'secondary',
                            thickness: 5,
                            size: 55,
                        }}
                        muiSkeletonProps={{
                            animation: 'pulse',
                            height: 28,
                        }}
                        enableTopToolbar={false}
                        enableFacetedValues={true}
                        enableDensityToggle={false}
                        enableHiding={false}
                        showSkeletons={false}
                        enableStickyHeader={true}
                        enableColumnFilterModes={false}
                        enableColumnActions={false}
                        enableColumnOrdering={false}
                        enableFullScreenToggle={false}
                        enableColumnFilters={false}
                        enablePagination={false}
                        initialState={{
                            showColumnFilters: false,
                            showGlobalFilter: false,
                        }}
                        muiTableBodyRowProps={{
                            sx: {
                                backgroundColor: '#f5f5f5', // Default row background color
                                '&:nth-of-type(odd)': {
                                    backgroundColor: '#ffffff', // Alternate rows with white
                                },
                                '&:hover': {
                                    backgroundColor: '#e0e0e0', // Hover effect
                                    cursor: 'pointer',
                                },
                            },
                        }}
                    />
                    <Box>
                        <Pagination
                            style={{ marginTop: '-40px', zIndex: 1000, position: 'relative', display: 'flex', justifyContent: 'flex-end' }}
                            count={totalPages}
                            color="primary"
                            page={currentPage}
                            onChange={(event, value) => setCurrentPage(value)}
                        />
                    </Box>
                </Card>
            </ThemeProvider>
            <LogoutExpiredModal setLogOutModal={setLogOutModal} logOutModal={logOutModal}/>
        </div>
    );
};
export default ReferralAgent;
